import type { CSSProperties } from 'react';
import { orgLogoUrl, orgName } from '../shell/shared';

/* ------------------------------------------------------------------ */
/*  ComparisonShareCard (PRD.240)                                     */
/*                                                                    */
/*  Patient-facing rendered output of a saved Before/After comparison */
/*  for WhatsApp delivery. PRD §5.5/§5.6/§7.3: lightweight clinic     */
/*  branding sits in a thin header strip; the clinical comparison     */
/*  takes the main visual weight. The PRD also notes (§7.4) that the  */
/*  shared image does not need to mirror the editor chrome — this is  */
/*  a clean, patient-friendly composition, not a screenshot of the    */
/*  workspace.                                                         */
/*                                                                    */
/*  V1 layout (always side-by-side regardless of editor mode):        */
/*                                                                    */
/*    ┌────────────────────────────────────────────┐                  */
/*    │ [logo] CLINIC NAME                  date   │ ← branding strip */
/*    ├────────────────────────────────────────────┤                  */
/*    │  BEFORE        │        AFTER             │                  */
/*    │  ┌─────────┐   │   ┌─────────┐           │                  */
/*    │  │  img    │   │   │  img    │           │                  */
/*    │  └─────────┘   │   └─────────┘           │                  */
/*    └────────────────────────────────────────────┘                  */
/*                                                                    */
/*  Render width is fixed at 1080px (WhatsApp's recommended image    */
/*  width). renderNodeToPng then captures it at 2× for crispness.    */
/* ------------------------------------------------------------------ */

export interface ComparisonShareCardProps {
  beforeImageUrl: string | null;
  afterImageUrl: string | null;
  /**
   * Optional transparent strokes overlays for drawing sides. When set, the
   * panel layers this on top of the base image (same approach as the on-screen
   * DrawCard) so a Firebase-hosted base photo is preserved in the capture.
   * Compositing the drawing into a single data URL up front would drop the
   * base, because Konva's `toDataURL` taints on cross-origin Firebase images.
   */
  beforeOverlayUrl?: string | null;
  afterOverlayUrl?: string | null;
  patientName?: string;
  /** ISO date to render in the header. Defaults to today. */
  dateIso?: string;
}

const CARD_WIDTH_PX = 1080;
const IMAGE_PANEL_HEIGHT_PX = 720;
const PANEL_GAP_PX = 12;

export function ComparisonShareCard({
  beforeImageUrl,
  afterImageUrl,
  beforeOverlayUrl,
  afterOverlayUrl,
  patientName,
  dateIso,
}: ComparisonShareCardProps) {
  const dateLabel = formatDate(dateIso ?? new Date().toISOString());

  return (
    <div style={cardStyle}>
      {/* Branding strip — subtle, single horizontal line */}
      <div style={headerStyle}>
        <div style={brandClusterStyle}>
          {orgLogoUrl && (
            <img
              src={orgLogoUrl}
              alt={orgName}
              style={{
                height: 36,
                width: 'auto',
                objectFit: 'contain',
                display: 'block',
              }}
            />
          )}
          <span style={brandNameStyle}>{orgName}</span>
        </div>
        <span style={metaStyle}>{dateLabel}</span>
      </div>

      {/* Optional patient line — kept lightweight, only when supplied */}
      {patientName && (
        <div style={patientLineStyle}>
          <span style={{ color: '#94a3b8', marginRight: 6 }}>Patient:</span>
          <strong style={{ color: '#0f172a' }}>{patientName}</strong>
        </div>
      )}

      {/* Side-by-side comparison */}
      <div style={panelRowStyle}>
        <ComparisonPanel label="BEFORE" imageUrl={beforeImageUrl} overlayUrl={beforeOverlayUrl} />
        <ComparisonPanel label="AFTER" imageUrl={afterImageUrl} overlayUrl={afterOverlayUrl} />
      </div>
    </div>
  );
}

/* ------------------------------------------------------------------ */

function ComparisonPanel({
  label,
  imageUrl,
  overlayUrl,
}: {
  label: string;
  imageUrl: string | null;
  overlayUrl?: string | null;
}) {
  return (
    <div style={panelStyle}>
      <div style={panelLabelStyle}>{label}</div>
      <div style={panelImageWrapStyle}>
        {imageUrl ? (
          // Both sides fill their panel with object-fit: cover, matching the
          // app's side-by-side view (AssetImage) so BEFORE and AFTER are framed
          // identically regardless of each source photo's aspect ratio. A base
          // photo + optional transparent strokes overlay are layered as two
          // bitmaps html2canvas composites cleanly (base via useCORS) — a
          // pre-flattened Konva export would drop the cross-origin base.
          <div style={layeredWrapStyle}>
            <img src={imageUrl} alt={label} style={fillImgStyle} />
            {overlayUrl && (
              <img src={overlayUrl} alt={`${label} annotations`} style={fillImgStyle} />
            )}
          </div>
        ) : (
          <div style={panelEmptyStyle}>No image</div>
        )}
      </div>
    </div>
  );
}

function formatDate(iso: string): string {
  try {
    const d = new Date(iso);
    return d.toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return '';
  }
}

/* ------------------------------------------------------------------ */
/*  Styles — inline to keep the share card self-contained for the    */
/*  offscreen capture. Inspired by the existing print layouts.        */
/* ------------------------------------------------------------------ */

const cardStyle: CSSProperties = {
  width: CARD_WIDTH_PX,
  background: '#ffffff',
  color: '#0f172a',
  fontFamily:
    'Inter, system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif',
  padding: '28px 32px 32px',
  boxSizing: 'border-box',
};

const headerStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 16,
  paddingBottom: 14,
  borderBottom: '1px solid #e2e8f0',
};

const brandClusterStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  gap: 12,
};

const brandNameStyle: CSSProperties = {
  fontSize: 18,
  fontWeight: 700,
  letterSpacing: 0.1,
  color: '#0f172a',
};

const metaStyle: CSSProperties = {
  fontSize: 13,
  color: '#64748b',
  letterSpacing: 0.2,
};

const patientLineStyle: CSSProperties = {
  marginTop: 12,
  fontSize: 14,
  color: '#475569',
};

const panelRowStyle: CSSProperties = {
  marginTop: 18,
  display: 'flex',
  gap: PANEL_GAP_PX,
};

const panelStyle: CSSProperties = {
  flex: 1,
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
};

const panelLabelStyle: CSSProperties = {
  fontSize: 12,
  fontWeight: 700,
  letterSpacing: '0.08em',
  color: '#64748b',
  textTransform: 'uppercase',
};

const panelImageWrapStyle: CSSProperties = {
  width: '100%',
  height: IMAGE_PANEL_HEIGHT_PX,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  background: '#f8fafc',
  border: '1px solid #e2e8f0',
  borderRadius: 8,
  overflow: 'hidden',
};

const panelEmptyStyle: CSSProperties = {
  fontSize: 14,
  color: '#94a3b8',
};

// Fill the panel and stack base + optional overlay so the strokes register
// exactly over the base (both use the same box + object-fit: cover).
const layeredWrapStyle: CSSProperties = {
  position: 'relative',
  width: '100%',
  height: '100%',
  background: '#fff',
};

const fillImgStyle: CSSProperties = {
  position: 'absolute',
  inset: 0,
  width: '100%',
  height: '100%',
  objectFit: 'cover',
  display: 'block',
};
